/**
 * The permission dot-strings `internal/eam` declares (C11 §6.1).
 *
 * Typed out here rather than assembled from fragments so a screen's `auth.can`
 * call and the route's `meta.permission` cannot drift from each other, and so a
 * rename shows up as a TypeScript error rather than as a button that quietly
 * stops appearing.
 *
 * Two are worth knowing before reading any screen in this module:
 *
 *  - **`eam.permit.issue` and `eam.permit.accept_return` are different people.**
 *    The permit-to-work board's three signatures are a three-way segregation of
 *    duties (§4.7): the person who requests is not the person who issues, and
 *    the person who issues is not the person who accepts the return. The board
 *    renders each control only for the holder of that step's permission, which
 *    is what makes the separation visible rather than merely enforced.
 *  - **There is no `eam.permit.execute` control anywhere in this module**, and
 *    that is deliberate: the executor is stamped by
 *    `POST /service-work-orders/:id/start`, at the moment work actually begins,
 *    so a permit cannot be "executed" by somebody who is not doing the work.
 */

export const EAM_PERMISSIONS = {
  assetRead: 'eam.asset.read',
  assetManage: 'eam.asset.manage',
  assetCommission: 'eam.asset.commission',
  assetDecommission: 'eam.asset.decommission',
  locationManage: 'eam.location.manage',
  planRead: 'eam.plan.read',
  planManage: 'eam.plan.manage',
  meterRead: 'eam.meter.read',
  meterRecord: 'eam.meter.record',
  failureRecord: 'eam.failure.record',
  complianceRead: 'eam.compliance.read',
  complianceManage: 'eam.compliance.manage',
  inspectionRecord: 'eam.inspection.record',
  permitRead: 'eam.permit.read',
  permitIssue: 'eam.permit.issue',
  permitAcceptReturn: 'eam.permit.accept_return',
  downtimeManage: 'eam.downtime.manage',
  reportRead: 'eam.report.read',
} as const

export type EamPermission = (typeof EAM_PERMISSIONS)[keyof typeof EAM_PERMISSIONS]
